import React from 'react'

import { ConfirmDialog, ConfirmDialogProps } from 'primereact/confirmdialog'

import { calculateBreakpointWidth } from '../../utils/helper/calculateBreakpointWidth'

type Severity = 'danger' | 'warning'

type Props = ConfirmDialogProps & {
  severity?: Severity
  isVisible: boolean
  header: string
  message: string | JSX.Element
  rejectLabel: string
  acceptLabel: string
  onHide: () => void
  onAccept: () => void
  onReject: () => void
  width?: number
}

/**
 * CustomConfirmDialog Component
 *
 * @param {('danger' | 'warning')} [severity] - The severity of the dialog, either 'danger' or 'warning'.
 * @param {boolean} isVisible - Indicates whether the dialog is visible.
 * @param {string} header - The header of the dialog. Should not contain a "?".
 * @param {string | JSX.Element} message - The message of the dialog.
 * @param {string} rejectLabel - The label for the reject button.
 * @param {string} acceptLabel - The label for the accept button.
 * @param {() => void} onHide - Function to call when the dialog is hidden.
 * @param {() => void} onAccept - Function to call when the accept button is clicked.
 * @param {() => void} onReject - Function to call when the reject button is clicked.
 * @param {number} [width=36] - The width of the dialog in rem.
 * @param {ConfirmDialogProps} rest - Additional properties passed to the ConfirmDialog component.
 *
 * @returns {JSX.Element} The CustomConfirmDialog component.
 */
export const CustomConfirmDialog: React.FC<Props> = ({
  severity,
  isVisible,
  header,
  message,
  rejectLabel,
  acceptLabel,
  onHide,
  onReject,
  onAccept,
  width = 36,
  ...rest
}) => {
  const getDialogProps = (severity?: Severity) => {
    switch (severity) {
      case 'danger':
        return {
          icon: 'pi pi-exclamation-triangle',
          acceptClassName: 'p-button-danger',
        }
      case 'warning':
        return {
          icon: 'pi pi-exclamation-triangle',
          acceptClassName: 'p-button-warning',
        }
      default:
        return {
          icon: 'pi pi-info-circle',
          acceptClassName: 'p-button',
        }
    }
  }

  const dialogProps = getDialogProps(severity)

  return (
    <ConfirmDialog
      visible={isVisible}
      header={header}
      message={message}
      rejectLabel={rejectLabel}
      acceptLabel={acceptLabel}
      onHide={onHide}
      reject={onReject}
      accept={onAccept}
      defaultFocus="reject"
      style={{ width: width + 'rem' }}
      breakpoints={{ [calculateBreakpointWidth(width)]: '90vw' }}
      {...dialogProps}
      {...rest}
    />
  )
}
